/**
 * Pestaña «Resumen» de la ficha de servidor en variante tarjetas (beta).
 *
 * Aquí viven Identificación y Ubicación: son las dos pestañas que se eliminan
 * en esta variante. Los campos se editan directamente en las tarjetas con el
 * mismo `handleChange` del formulario padre, así que no hay estado duplicado
 * ni una segunda pantalla para lo mismo.
 *
 * La monitorización NO se repite aquí: su tarjeta de salud vive en la pestaña
 * «Monitorización», que ya tiene los gráficos y el histórico completos.
 */
import React, { useMemo } from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';
import ServerLiveMonitor from './ServerLiveMonitor';
import LocationTreeSelect from './common/LocationTreeSelect';
import { Valor, CampoTexto, CampoSelect, opcionesDeCatalogo } from './common/Sf2Fields';
import { buildLocationMap, locationPath } from '../utils/locationTree';

const OPCIONES_ENTORNO = [
  { value: 'prod', label: 'Producción' },
  { value: 'pre', label: 'Pre-producción' },
  { value: 'dev', label: 'Desarrollo' },
];

const OPCIONES_ESTADO = [
  { value: 'active', label: 'Activo' },
  { value: 'maintenance', label: 'Mantenimiento' },
  { value: 'decom', label: 'Decom' },
];

export default function ServerFormSummary({
  form,
  editing = false,
  onChange,
  onLocationChange,
  onRackChange,
  onGo,
  locations = [],
  racks = [],
  isNode = false,
  rangeLabel,
  chassisName,
  hasBmcComponent = false,
  bmcIp = '',
  hypervisorLabel = '',
  serverId = null,
  companyId = null,
  hasAgent = false,
  counts = {},
  manufacturerOptions = [],
  modelOptions = [],
  onManufacturerChange = null,
  onCreateManufacturer = null,
  onCreateModel = null,
  onSuggestName = null,
  suggestingName = false,
}) {
  const completitud = useMemo(() => {
    const checks = [
      { label: 'Hostname', ok: Boolean(form.hostname) },
      { label: 'Sistema operativo', ok: Boolean(form.os) },
      { label: 'Fabricante', ok: Boolean(form.manufacturer) },
      { label: 'Modelo', ok: Boolean(form.model) },
      { label: 'Nº de serie', ok: Boolean(form.serial_number) },
      { label: 'Ubicación', ok: Boolean(form.location_id) },
      { label: 'Rack', ok: Boolean(form.rack_id) },
      { label: 'Posición U', ok: Boolean(form.position_u) },
      { label: 'Roles', ok: Number(counts.roles || 0) > 0 },
      { label: 'IP BMC / gestión', ok: Boolean(form.ip_address || bmcIp) },
      { label: 'Licencia', ok: Boolean(form.license) },
      { label: 'CPU', ok: Boolean(form.cpu) },
      { label: 'RAM', ok: Boolean(form.ram_gb) },
    ];
    const hechos = checks.filter((c) => c.ok).length;
    return {
      pct: Math.round((hechos / checks.length) * 100),
      faltan: checks.filter((c) => !c.ok).map((c) => c.label),
    };
  }, [form, bmcIp, counts.roles]);

  const ipBmc = hasBmcComponent ? (bmcIp || form.ip_address) : form.ip_address;
  const opcionesRack = [
    { value: '', label: '— Sin rack —' },
    ...racks.map((r) => ({ value: r.id, label: r.name })),
  ];
  const opcionesFormFactor = [
    { value: 'rack', label: 'Rack' },
    { value: 'tower', label: 'Torre' },
    { value: 'node', label: 'Nodo de chasis' },
  ];
  const opcionesFabricante = opcionesDeCatalogo(manufacturerOptions);
  const opcionesModelo = opcionesDeCatalogo(modelOptions);

  return (
    <div className="sf2-summary">
      <ServerLiveMonitor
        serverId={serverId}
        companyId={companyId}
        hasAgent={hasAgent}
        onGo={onGo}
      />

      <div className="sf2-grid">
        <div className="sf2-card">
          <h3>Identificación<span className="sf2-count">editable</span></h3>
          <div className="sf2-fields">
            <CampoTexto
              label="Nombre"
              name="name"
              value={form.name}
              onChange={onChange}
              editing={editing}
              placeholder="srv-app-01"
              required
              action={onSuggestName ? (
                <button
                  type="button"
                  className="sf2-icon-btn"
                  title="Generar nombre con la nomenclatura"
                  aria-label="Generar nombre con la nomenclatura"
                  onClick={onSuggestName}
                  disabled={suggestingName}
                >
                  {suggestingName ? <span className="dv-spinner" /> : <SparklesIcon style={{ width: 16, height: 16 }} />}
                </button>
              ) : null}
            />
            <CampoTexto
              label="Hostname"
              name="hostname"
              value={form.hostname}
              onChange={onChange}
              editing={editing}
              placeholder="srv-app-01.example.com"
            />
            <CampoSelect
              label="Fabricante"
              name="manufacturer"
              value={form.manufacturer}
              onChange={onManufacturerChange
                ? (e) => onManufacturerChange(e.target.value)
                : onChange}
              editing={editing}
              options={opcionesFabricante}
              onAdd={onCreateManufacturer}
              addPlaceholder="Nuevo fabricante"
              addLabel="Añadir fabricante al catálogo"
            />
            <CampoSelect
              label="Modelo"
              name="model"
              value={form.model}
              onChange={onChange}
              editing={editing}
              options={opcionesModelo}
              onAdd={onCreateModel}
              addPlaceholder="Nuevo modelo"
              addLabel="Añadir modelo al catálogo"
            />
            <CampoTexto
              label="Nº de serie"
              name="serial_number"
              value={form.serial_number}
              onChange={onChange}
              editing={editing}
              placeholder="SN123456789"
            />
            <CampoTexto
              label="Asset tag"
              name="asset_tag"
              value={form.asset_tag}
              onChange={onChange}
              editing={editing}
              placeholder="AST-0001"
            />
            <CampoTexto
              label="Sistema operativo"
              name="os"
              value={form.os}
              onChange={onChange}
              editing={editing}
              placeholder="Windows Server 2022"
            />
            <CampoSelect
              label="Entorno"
              name="environment"
              value={form.environment}
              onChange={onChange}
              editing={editing}
              options={OPCIONES_ENTORNO}
            />
            <CampoSelect
              label="Estado"
              name="status"
              value={form.status}
              onChange={onChange}
              editing={editing}
              options={OPCIONES_ESTADO}
            />
            {hasBmcComponent ? (
              <Valor label="IP BMC / iLO">{ipBmc}</Valor>
            ) : (
              <CampoTexto
                label="IP BMC / iLO"
                name="ip_address"
                value={form.ip_address}
                onChange={onChange}
                editing={editing}
                placeholder="10.0.0.1"
              />
            )}
          </div>
          {hasBmcComponent && (
            <div className="sf2-hint" style={{ marginTop: 10 }}>
              ✓ Interfaz BMC creada ·{' '}
              <button type="button" className="sf2-link" onClick={() => onGo('red')}>Gestionar en Red / IP</button>
            </div>
          )}
        </div>

        <div className="sf2-card">
          <h3>Ubicación<span className="sf2-count">editable</span></h3>
          <div className="sf2-fields">
            <div className="sf2-field">
              <label>Ubicación</label>
              {editing ? (
                <LocationTreeSelect
                  locations={locations}
                  value={form.location_id}
                  onChange={(id) => (onLocationChange || onChange)({ target: { name: 'location_id', value: id } })}
                  placeholder="— Sin ubicación —"
                />
              ) : (
                <div className="v">
                  {form.location_id
                    ? locationPath(buildLocationMap(locations), form.location_id) || '—'
                    : '—'}
                </div>
              )}
            </div>
            <CampoSelect
              label="Rack"
              name="rack_id"
              value={form.rack_id}
              onChange={onRackChange || onChange}
              editing={editing}
              options={opcionesRack}
            />
            {!isNode && (
              <>
                <CampoTexto
                  label="Posición U"
                  name="position_u"
                  value={form.position_u}
                  onChange={onChange}
                  editing={editing}
                  placeholder="12"
                />
                <CampoTexto
                  label="Tamaño U"
                  name="size_u"
                  value={form.size_u}
                  onChange={onChange}
                  editing={editing}
                  placeholder="2"
                />
              </>
            )}
            <CampoSelect
              label="Factor de forma"
              name="form_factor"
              value={form.form_factor}
              onChange={onChange}
              editing={editing}
              options={opcionesFormFactor}
            />
            {isNode && <Valor label="Chasis" muted={!chassisName}>{chassisName}</Valor>}
          </div>
          {rangeLabel && (
            <div className="sf2-hint" style={{ marginTop: 10 }}>Ocupa {rangeLabel} del rack.</div>
          )}
        </div>
      </div>

      <div className="sf2-grid">
        <div className="sf2-card">
          <h3>Hardware y virtualización</h3>
          <div className="sf2-fields">
            <CampoTexto
              label="CPU"
              name="cpu"
              value={form.cpu}
              onChange={onChange}
              editing={editing}
              placeholder="2 × 8 cores Xeon"
            />
            <CampoTexto
              label="RAM (GB)"
              name="ram_gb"
              value={form.ram_gb}
              onChange={onChange}
              editing={editing}
              placeholder="64"
              type="number"
            />
            <CampoTexto
              label="Almacenamiento (TB)"
              name="storage_tb"
              value={form.storage_tb}
              onChange={onChange}
              editing={editing}
              placeholder="2"
              type="number"
            />
            <CampoTexto
              label="Hipervisor"
              name="hypervisor"
              value={form.hypervisor}
              onChange={onChange}
              editing={editing}
              placeholder="Hyper-V / VMware"
            />
            <Valor label={form.part_of_domain ? 'Dominio' : 'Grupo de trabajo'}>
              {form.domain_name || '—'}
            </Valor>
            <Valor label="Detectado por el agente" muted={!hypervisorLabel}>
              {hypervisorLabel || 'No detectado'}
            </Valor>
          </div>
        </div>

        <div className="sf2-card">
          <h3>Completitud de la ficha<span className="sf2-count">{completitud.pct}%</span></h3>
          <div className="sf2-bar"><i style={{ width: `${completitud.pct}%` }} /></div>
          <p style={{ margin: '10px 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            {completitud.faltan.length
              ? `Faltan: ${completitud.faltan.join(', ')}.`
              : 'Ficha completa.'}
          </p>
        </div>
      </div>
    </div>
  );
}
